import { useMemo } from 'react';

import { type Merchant, useMerchantsListQuery } from '~entities/merchant';
import type { Pos } from '~entities/pos';
import { useGetPointsOfSalesQuery } from '~entities/pos';
import {
  useGetSummaryTableQuery,
  type SummaryTableParameters,
  type GroupByField,
} from '~entities/summary';

import type { SummaryTableData } from './summary-table-data';

type SuccessfulReturn = {
  isSuccess: true;
  data: { items: SummaryTableData[]; total: number };
};

type UnsuccessfulReturn = {
  isSuccess: false;
  data: undefined;
};

type DefaultReturn = {
  isLoading: boolean;
  isError: boolean;
};

type UseSummaryTableDataReturn = DefaultReturn &
  (SuccessfulReturn | UnsuccessfulReturn);

export type SummaryQueryParameters = {
  searchParams: SummaryTableParameters;
  groupBy: GroupByField;
};

// query for getting enriched data to display in tables
export function useSummaryTableData(
  params?: SummaryQueryParameters
): UseSummaryTableDataReturn {
  const queries = [
    useGetSummaryTableQuery(
      params?.groupBy as GroupByField,
      params?.searchParams as SummaryTableParameters,
      {
        enabled: !!params,
      }
    ),
    useMerchantsListQuery(),
    useGetPointsOfSalesQuery(),
  ] as const;
  const [dataQuery, merchantsQuery, posQuery] = queries;

  const posMap = useMemo(
    () =>
      posQuery.data?.reduce(
        (result, pos) => {
          result[pos.id] = pos;

          return result;
        },
        {} as Record<string, Pos>
      ) ?? {},
    [posQuery.data]
  );
  const merchantMap = useMemo(
    () =>
      merchantsQuery.data?.reduce(
        (result, merchant) => {
          result[merchant.id] = merchant;

          return result;
        },
        {} as Record<string, Merchant>
      ) ?? {},
    [merchantsQuery.data]
  );

  const isQueryLoading = queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);
  const isFetching = queries.some((q) => q.isFetching);
  const isSuccess = queries.every((q) => q.isSuccess);
  const isLoading =
    isQueryLoading || (isFetching && dataQuery.isPlaceholderData);

  if (isSuccess) {
    const { items, total } = dataQuery.data as {
      total: number;
      items: SummaryTableData[];
    };

    const enrichedSummary: SummaryTableData[] = items.map((item) => {
      return {
        ...item,
        merchantName: merchantMap[item.merchantId]?.name,
        posName: item.pointOfSalesId
          ? posMap[item.pointOfSalesId]?.name
          : undefined,
      };
    });

    return {
      isSuccess: true,
      data: { items: enrichedSummary, total },
      isLoading,
      isError,
    };
  }

  return {
    isSuccess: false,
    data: undefined,
    isLoading,
    isError,
  };
}
